import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useUser } from "../../context/UserContext";
import { renewToken } from "../../services/authManager";
import FullPageLoader from "./FullPageLoader";

const PHONE_PATHS = ["/phone-registration", "/phone-verification"];

// Guard decidido apenas pelo estado atual (sem flags de "checking"): cada render
// resulta em loader, redirect (<Navigate>) ou <Outlet>, então nunca fica preso
// aguardando um efeito que não reexecuta.
const AuthGuard = () => {
  const auth = useAuth();
  const { user, error, initialized, loading, fetchUserData } = useUser();
  const location = useLocation();
  const [renewFailed, setRenewFailed] = useState(false);

  // Sessão expirada (ou ausente): tenta renovar via refresh_token uma vez.
  // Sucesso → USER_LOADED torna isAuthenticated true. Falha → login.
  useEffect(() => {
    if (auth.isAuthenticated || renewFailed) return;
    renewToken().catch((err) => {
      console.warn("[AUTH] Silent renew failed in AuthGuard:", err);
      setRenewFailed(true);
    });
  }, [auth.isAuthenticated, renewFailed]);

  const currentUrl = location.pathname + location.search;
  // Destino pós-onboarding de telefone (a raiz é o padrão das telas de telefone)
  const redirectTo = currentUrl === "/" ? null : currentUrl;

  if (!auth.isAuthenticated) {
    return renewFailed ? (
      <Navigate to="/login" replace state={{ from: currentUrl }} />
    ) : (
      <FullPageLoader />
    );
  }

  // Páginas de telefone não dependem dos dados do /me
  if (PHONE_PATHS.includes(location.pathname)) {
    return <Outlet />;
  }

  if (!initialized && error && !loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-4 text-center">
          <p className="text-slate-600">
            Não foi possível carregar seus dados. Verifique sua conexão e
            tente novamente.
          </p>
          <button
            onClick={() => fetchUserData(true)}
            className="bg-slate-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 focus:ring-2 focus:ring-slate-700/20 focus:outline-none transition-all duration-200"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!initialized) {
    return <FullPageLoader />;
  }

  // Comprador usa o app sem telefone (pedido só ao enviar cotação: useRequireVerifiedPhone).
  // Onboarding de vendedor (storeId no state do login, fluxo /store-registration, ou na URL,
  // deep link de cotação) continua passando pelo cadastro de telefone e volta para a URL atual.
  const storeId =
    auth.user?.state?.storeId ||
    new URLSearchParams(location.search).get("storeId");
  const needsPhone = !!storeId || !!user?.storeId;

  if (needsPhone && !user?.phoneNumber) {
    const phoneRegPath = storeId
      ? `/phone-registration?storeId=${storeId}`
      : "/phone-registration";
    return (
      <Navigate to={phoneRegPath} replace state={{ redirectTo }} />
    );
  }

  if (needsPhone && !user.phoneVerified) {
    return (
      <Navigate
        to="/phone-verification"
        replace
        state={{ redirectTo }}
      />
    );
  }

  return <Outlet />;
};

export default AuthGuard;
